import React, { useEffect } from 'react';
import useAuth from '@jetshop/core/components/AuthContext/useAuth';
import { useQuery } from 'react-apollo';
import UserDataQuery from './UserDataQuery.gql';
import { initializeUserData, updateUserData } from './userData';

/**
 * Component to initialize and update user data in dataLayer
 * Should be rendered high in the component tree
 * 
 * Per client specification:
 * - Initializes before GTM loads
 * - Updates on login/logout during session
 * - Uses non-identifiable persistent user_id (externalId)
 * - Sets unavailable fields to undefined
 */
export function UserDataProvider() {
  const { loggedIn } = useAuth();
  const { data, loading, error } = useQuery(UserDataQuery, {
    skip: !loggedIn,
    fetchPolicy: 'cache-first'
  });

  // Calculate membership status based on bonus points
  // Returns number (1, 2, 3, 4) or undefined
  // If user has ANY points (even 1), they are at least level 1
  const getMembershipStatus = (points) => {
    // If points is null, undefined, or user is not logged in
    if (points === null || points === undefined) return undefined;
    
    // If points is 0 or less, but user is logged in, they still might be member level 1
    if (points === 0) return 1; // Default member level
    
    // Otherwise calculate based on points
    if (points < 1000) return 1; // Bronze
    if (points < 3000) return 2; // Silver
    if (points < 5000) return 3; // Gold
    return 4; // Platinum
  };

  // Track if we've initialized to avoid duplicate pushes
  const hasInitializedRef = React.useRef(false);

  // Initialize and update user data
  useEffect(() => {
    // If logged in and still loading data, wait for it to complete
    if (loggedIn && loading) {
      return;
    }

    const user_id = data?.customer?.externalId;
    const points = data?.customerLoyalty?.bonusPoints?.points;
    
    // If logged in but no loyalty data, default to level 1
    let membership_status = undefined;
    if (loggedIn) {
      membership_status = points !== undefined ? getMembershipStatus(points) : 1; // Default to level 1 for logged in users
    }

    // Use initializeUserData only on first call, then updateUserData
    if (!hasInitializedRef.current) {
      initializeUserData({
        logged_in: loggedIn,
        user_id: user_id,
        membership_status: membership_status
      });
      hasInitializedRef.current = true;
    } else {
      updateUserData({
        logged_in: loggedIn,
        user_id: user_id,
        membership_status: membership_status
      });
    }
  }, [loggedIn, data, loading, error]);

  return null; // This component doesn't render anything
}

